'use client';

import SvgDelete from '@common/__generated__/icon/SvgDelete';
import { Button, Chip } from '@common/components';
import { cn } from '@core/utils';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { UnauthQueryOptions } from '@/api';

import type { RecentHistory } from '../_utils/recent-history';
import { recentHistory } from '../_utils/recent-history';

interface SearchPrepareSectionProps {
  /** 최근 검색어 클릭 시 */
  onHistoryClick?: (keyword: string, category: '최근 검색어') => void;
  /** 추천 검색어 클릭 시 */
  onRecommendClick?: (keyword: string, category: '추천 검색어') => void;
  className?: string;
}

export const SearchPrepareSection = ({
  onHistoryClick,
  onRecommendClick,
  className,
}: SearchPrepareSectionProps) => {
  const { get, clear, delete: deleteHistory } = recentHistory;
  const [recentHistoryKeywords, setRecentHistoryKeywords] = useState<
    RecentHistory[]
  >([]);

  //* 최근 검색어
  useEffect(() => {
    setRecentHistoryKeywords(get());
  }, [get]);

  //* 추천 검색어
  const { data: recommendKeywords } = useSuspenseQuery({
    ...UnauthQueryOptions['get:/wello/v2/allows/home-search-keyword']({
      query: {
        homeSearchKeywordType: 'SEARCH_GIFT_PRODUCT',
      },
    }),
    select: (data) => data.context?.contents ?? [],
  });

  const isDisplayRecentHistory = recentHistoryKeywords.length > 0;
  const isDisplayRecommendKeywords =
    recommendKeywords && recommendKeywords.length > 0;

  return (
    <section className={cn('flex flex-col gap-40 px-20 py-16', className)}>
      {isDisplayRecentHistory ? (
        <div className="flex flex-col gap-16">
          <div className="flex items-center justify-between">
            <p className="font-600 text-Gray-900 typo-body4">최근 검색어</p>
            <Button
              theme="sub-text"
              className="size-fit p-0 font-500 text-Gray-550 typo-body4"
              onClick={() => {
                clear();
                setRecentHistoryKeywords([]);
              }}
            >
              모두 지우기
            </Button>
          </div>
          <ul className="flex flex-wrap gap-8">
            {recentHistoryKeywords.map(({ keyword, id }) => (
              <li key={id}>
                <Chip
                  theme="primary-line"
                  onClick={() => onHistoryClick?.(keyword, '최근 검색어')}
                  className="gap-4 font-400"
                >
                  {keyword}
                  <Button
                    theme="sub-text"
                    className="mb-2 size-fit p-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteHistory(id);
                      setRecentHistoryKeywords(get());
                    }}
                  >
                    <SvgDelete className="size-14 text-Gray-700" />
                  </Button>
                </Chip>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <></>
      )}

      {isDisplayRecommendKeywords ? (
        <div className="flex flex-col gap-16">
          <p className="font-600 text-Gray-900 typo-body4">추천 검색어</p>

          <ul className="flex flex-wrap gap-8">
            {recommendKeywords?.map(({ keyword }) => {
              if (!keyword) return null;

              return (
                <li key={keyword}>
                  <Chip
                    theme="sub"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRecommendClick?.(keyword, '추천 검색어');
                    }}
                    className="gap-4 border-none bg-B-25 font-400 text-B-500"
                  >
                    {keyword}
                  </Chip>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
    </section>
  );
};
